import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DollarSign, RefreshCw, TrendingUp, Layers, Database, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  api,
  type CostLedgerView,
  type CostSummaryBreakdown,
  type CostProvider,
  type AwsBedrockDailyCost,
} from "../../lib/api.js";

/**
 * Spend page — the central cost dashboard. Reads from `cost_ledger` (migration 020).
 *
 * Layout:
 *   - Time-window selector (MTD default, 7d, 30d, all-time, custom)
 *   - 3 KPI tiles: total spend, top provider, top operation
 *   - Stacked donut-ish table: per-provider with % bars + spend totals
 *   - Per-operation table (top 20)
 *   - Daily/hourly timeseries (text-only sparkline-style; full chart can land later)
 *   - Recent ledger entries (200 rows, filterable)
 *
 * Polls every 60s — cost data doesn't change second-to-second.
 */

type Window = "mtd" | "7d" | "30d" | "all";

function windowToRange(w: Window): { since?: string; until?: string } {
  const now = new Date();
  if (w === "mtd") return {};
  if (w === "all") return { since: "2025-01-01T00:00:00.000Z" };
  const ms = w === "7d" ? 7 * 86_400_000 : 30 * 86_400_000;
  return { since: new Date(now.getTime() - ms).toISOString() };
}

export function SpendPage() {
  const [windowKey, setWindowKey] = useState<Window>("mtd");
  const range = useMemo(() => windowToRange(windowKey), [windowKey]);

  const summary = useQuery({
    queryKey: ["cost", "summary", windowKey],
    queryFn: () => api.costSummary(range),
    refetchInterval: 60_000,
  });
  const series = useQuery({
    queryKey: ["cost", "timeseries", windowKey],
    queryFn: () => api.costTimeseries({ ...range, bucket: windowKey === "7d" ? "day" : "day" }),
    refetchInterval: 60_000,
  });
  const recent = useQuery({
    queryKey: ["cost", "recent", windowKey],
    queryFn: () => api.costRecent({ limit: 200 }),
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Spend</h1>
          <p className="mt-1 text-sm text-muted">
            Per-call cost log across every paid provider. Refreshes every 60s.
            {summary.data?.range && (
              <span className="ml-2 text-2xs text-muted/70">
                {summary.data.range.since.slice(0, 10)} → {summary.data.range.until.slice(0, 10)}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <WindowPicker value={windowKey} onChange={setWindowKey} />
          <button
            onClick={() => { summary.refetch(); series.refetch(); recent.refetch(); }}
            className="btn-ghost flex items-center gap-1 text-sm"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiTile
          icon={<DollarSign className="h-5 w-5" />}
          label="Total spend"
          value={`$${(summary.data?.totalUsd ?? 0).toFixed(2)}`}
          subtle={`${summary.data?.rowCount ?? 0} calls logged`}
        />
        <KpiTile
          icon={<Layers className="h-5 w-5" />}
          label="Top provider"
          value={summary.data?.byProvider[0]?.key ?? "—"}
          subtle={summary.data?.byProvider[0] ? `$${summary.data.byProvider[0].totalUsd.toFixed(2)}` : ""}
        />
        <KpiTile
          icon={<TrendingUp className="h-5 w-5" />}
          label="Top operation"
          value={summary.data?.byOperation[0]?.key ?? "—"}
          subtle={summary.data?.byOperation[0] ? `$${summary.data.byOperation[0].totalUsd.toFixed(2)}` : ""}
        />
      </div>

      {/* Actual AWS-billed Bedrock cost (Cost Explorer) */}
      <AwsBedrockSection />

      {/* Per-provider table */}
      <Section title="By provider">
        {summary.data && summary.data.byProvider.length > 0 ? (
          <BreakdownTable rows={summary.data.byProvider} total={summary.data.totalUsd} />
        ) : (
          <Empty>No spend captured in this window.</Empty>
        )}
      </Section>

      {/* Per-operation table */}
      <Section title="By operation · top 20">
        {summary.data && summary.data.byOperation.length > 0 ? (
          <BreakdownTable rows={summary.data.byOperation} total={summary.data.totalUsd} />
        ) : (
          <Empty>No operations recorded.</Empty>
        )}
      </Section>

      {/* Timeseries */}
      <Section title="Daily timeline">
        {series.data && series.data.points.length > 0 ? (
          <Sparkline points={series.data.points} />
        ) : (
          <Empty>No timeline points in this window.</Empty>
        )}
      </Section>

      {/* Recent rows */}
      <Section title="Recent calls">
        {recent.data && recent.data.rows.length > 0 ? (
          <RecentTable rows={recent.data.rows} />
        ) : (
          <Empty>No calls in the ledger yet.</Empty>
        )}
      </Section>

      {/* Backup status — operational health */}
      <Section title="SQLite backups">
        <BackupPanel />
      </Section>

      {/* Operator data — portable migration safety net */}
      <Section title="Operator data (export / import)">
        <OperatorDataPanel />
      </Section>

      {/* Planning-data sync — merge-safe local → prod (clusters, memberships, entities, metrics) */}
      <Section title="Planning-data sync (local → this DB)">
        <PlanningDataPanel />
      </Section>

      {/* Media catalogue — the generated layer: library clips and storyboards */}
      <Section title="Media catalogue sync (local → this DB)">
        <MediaCataloguePanel />
      </Section>
    </div>
  );
}

function OperatorDataPanel() {
  const [report, setReport] = useState<null | { matched: number; updated: number; unmatched: number; alwaysLinks: number }>(null);
  const imp = useMutation({
    mutationFn: async (file: File) => {
      const text = await file.text();
      const data = JSON.parse(text) as unknown;
      const res = await api.operatorDataImport(data);
      if (!res.ok || !res.report) throw new Error(res.error || "import failed");
      return res.report;
    },
    onSuccess: (r) => setReport({
      matched: r.articleKeywords.matched,
      updated: r.articleKeywords.updated,
      unmatched: r.articleKeywords.unmatched.length,
      alwaysLinks: r.alwaysLinks.upserted,
    }),
  });

  return (
    <div className="space-y-4 text-sm">
      <div className="rounded-md border border-line bg-canvas p-3">
        <div className="font-medium">Move the whole platform</div>
        <p className="mt-1 text-muted">
          Every tab's data (clusters, keywords, content DB, produced articles, costs, audits, entities…) lives in one SQLite
          database. Download it for a complete structured-data transfer. For a full transfer <em>including generated media</em>
          (images/videos/voiceovers/viral renders), use the CLI:
          <code className="ml-1 rounded bg-line/40 px-1">keywords data-bundle export bundle.tar.gz</code>.
        </p>
        <p className="mt-1 text-xs text-muted">
          Migrating to AWS? Run <code className="rounded bg-line/40 px-1">keywords data-bundle guide</code> for the exact
          steps with your paths filled in, or read <code className="rounded bg-line/40 px-1">docs/AWS_MIGRATION.md</code>.
          The golden rule: <strong className="text-fg">replace</strong> only a fresh box;
          <strong className="text-fg"> merge</strong> (operator-data import) when AWS already has data.
        </p>
        <a href={api.dbSnapshotUrl} download className="btn-ghost mt-2 inline-flex items-center gap-1">
          <Database className="h-4 w-4" /> Download full database (.db)
        </a>
      </div>

      <p className="text-muted">
        Or, for a surgical, git-versionable safety net: your hand-entered keywords (article main/secondary KWs) and always-link
        targets are <strong className="text-fg">not</strong> recoverable from Sanity. Export them to a small JSON file, then
        re-import after a sync — matching is by Sanity ID, so it survives a fresh sync.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <a href={api.operatorDataExportUrl} download className="btn-ghost flex items-center gap-1">
          <Database className="h-4 w-4" /> Export operator data (JSON)
        </a>
        <label className="btn-ghost flex cursor-pointer items-center gap-1">
          <RefreshCw className="h-4 w-4" /> Import…
          <input
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) { setReport(null); imp.mutate(f); }
              e.target.value = "";
            }}
          />
        </label>
        {imp.isPending && <span className="flex items-center gap-1 text-xs text-muted"><Loader2 className="h-3 w-3 animate-spin" /> importing…</span>}
      </div>
      {imp.error && <div className="text-xs text-danger">Import failed: {(imp.error as Error).message}</div>}
      {report && (
        <div className="rounded-md border border-line bg-canvas p-3 text-xs">
          <div className="flex items-center gap-1 font-medium text-accent"><CheckCircle2 className="h-3.5 w-3.5" /> Import applied</div>
          <div className="mt-1 text-muted">
            Article KWs: {report.updated} updated / {report.matched} matched
            {report.unmatched > 0 && <span className="text-warn"> · {report.unmatched} unmatched (run a Sanity sync first)</span>}
            {" · "}always-links: {report.alwaysLinks}
          </div>
        </div>
      )}
    </div>
  );
}

type PlanReport = NonNullable<Awaited<ReturnType<typeof api.planningDataImport>>["report"]>;

function PlanningReportTable({ r }: { r: PlanReport }) {
  const rows: [string, string][] = [
    ["Clusters", `${r.clusters.created} created · ${r.clusters.updated} updated`],
    ["Keywords", `${r.keywords.created} created · ${r.keywords.updated} updated`],
    ["Cluster primaries", `${r.clusterPrimary.set} set · ${r.clusterPrimary.skipped} skipped`],
    ["Entities", `${r.entities.created} created · ${r.entities.updated} updated`],
    ["Keyword metrics", `${r.keywordMetrics.upserted} upserted`],
    ["Always-links", `${r.alwaysLinks.upserted} upserted`],
  ];
  return (
    <table className="w-full text-xs">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-t border-line/60">
            <td className="py-1 pr-3 text-muted">{k}</td>
            <td className="py-1 tabular-nums">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PlanningDataPanel() {
  const [staged, setStaged] = useState<{ data: unknown; dry: PlanReport } | null>(null);
  const [applied, setApplied] = useState<PlanReport | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const preview = useMutation({
    mutationFn: async (file: File) => {
      const data = JSON.parse(await file.text()) as unknown;
      const res = await api.planningDataImport(data, { dryRun: true });
      if (!res.ok || !res.report) throw new Error(res.error || "preview failed");
      return { data, report: res.report };
    },
    onSuccess: ({ data, report }) => { setStaged({ data, dry: report }); setApplied(null); setErr(null); },
    onError: (e) => setErr((e as Error).message),
  });
  const apply = useMutation({
    mutationFn: async (data: unknown) => {
      const res = await api.planningDataImport(data, { dryRun: false });
      if (!res.ok || !res.report) throw new Error(res.error || "import failed");
      return res.report;
    },
    onSuccess: (r) => { setApplied(r); setStaged(null); setErr(null); },
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <div className="space-y-4 text-sm">
      <p className="text-muted">
        The <strong className="text-fg">planning layer</strong> you build locally — clusters, keyword→cluster memberships,
        cluster primaries, the Entity Hub, keyword metrics and always-links — none of which is in Sanity. Export it here, then
        Import it on another environment (e.g. AWS) to merge it in. <strong className="text-fg">Merge-safe &amp; one-way</strong>:
        it upserts by key, never deletes, and never touches produced articles, costs, distributions, or GSC. Import shows a
        dry-run preview first; a hot backup is taken before any real apply.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <a href={api.planningDataExportUrl} download className="btn-ghost flex items-center gap-1">
          <Database className="h-4 w-4" /> Export planning data (JSON)
        </a>
        <label className="btn-ghost flex cursor-pointer items-center gap-1">
          <RefreshCw className="h-4 w-4" /> Import… (preview first)
          <input
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) { setApplied(null); preview.mutate(f); } e.target.value = ""; }}
          />
        </label>
        {preview.isPending && <span className="flex items-center gap-1 text-xs text-muted"><Loader2 className="h-3 w-3 animate-spin" /> previewing…</span>}
      </div>
      {err && <div className="text-xs text-danger">Failed: {err}</div>}

      {staged && (
        <div className="rounded-md border border-warn bg-warn/5 p-3 text-xs">
          <div className="flex items-center gap-1 font-medium"><AlertCircle className="h-3.5 w-3.5 text-warn" /> Dry-run preview — nothing written yet</div>
          <div className="mt-2"><PlanningReportTable r={staged.dry} /></div>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={() => apply.mutate(staged.data)}
              disabled={apply.isPending}
              className="flex items-center gap-1 rounded bg-brand px-3 py-1.5 text-xs text-white hover:bg-brand/90 disabled:opacity-60"
            >
              {apply.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Apply to this database
            </button>
            <button onClick={() => setStaged(null)} className="btn-ghost text-xs">Cancel</button>
          </div>
        </div>
      )}
      {applied && (
        <div className="rounded-md border border-line bg-canvas p-3 text-xs">
          <div className="flex items-center gap-1 font-medium text-accent"><CheckCircle2 className="h-3.5 w-3.5" /> Applied to this database</div>
          <div className="mt-2"><PlanningReportTable r={applied} /></div>
        </div>
      )}
    </div>
  );
}

type CatalogueReport = NonNullable<Awaited<ReturnType<typeof api.mediaCatalogueImport>>["report"]>;

/**
 * The generated-media layer, moved between environments.
 *
 * Reels are planned and filmed wherever the operator is, and the clips are
 * durable — Drive and Sanity's CDN hold the bytes. What was not durable is the
 * rows that make them FINDABLE: the tags, the reuse measurements, and every
 * storyboard, all of which lived in one SQLite file. A library nobody can query
 * is a folder.
 *
 * The same shape as the planning sync deliberately — export, dry-run, apply —
 * but a separate endpoint, because the planning sync guarantees it never touches
 * runtime data and this is runtime data.
 */
function MediaCataloguePanel() {
  const [staged, setStaged] = useState<{ data: unknown; dry: CatalogueReport } | null>(null);
  const [applied, setApplied] = useState<CatalogueReport | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const preview = useMutation({
    mutationFn: async (file: File) => {
      const data = JSON.parse(await file.text()) as unknown;
      const res = await api.mediaCatalogueImport(data, { dryRun: true });
      if (!res.ok || !res.report) throw new Error(res.error || "preview failed");
      return { data, report: res.report };
    },
    onSuccess: ({ data, report }) => { setStaged({ data, dry: report }); setApplied(null); setErr(null); },
    onError: (e) => setErr((e as Error).message),
  });
  const apply = useMutation({
    mutationFn: async (data: unknown) => {
      const res = await api.mediaCatalogueImport(data, { dryRun: false });
      if (!res.ok || !res.report) throw new Error(res.error || "import failed");
      return res.report;
    },
    onSuccess: (r) => { setApplied(r); setStaged(null); setErr(null); },
    onError: (e) => setErr((e as Error).message),
  });

  const Report = ({ r }: { r: CatalogueReport }) => (
    <div className="space-y-2">
      <table className="w-full text-xs">
        <tbody>
          <tr className="border-t border-line/60">
            <td className="py-1 pr-3 text-muted">Library clips</td>
            <td className="py-1 tabular-nums">{r.clipsInserted} inserted · {r.clipsUpdated} updated</td>
          </tr>
          <tr className="border-t border-line/60">
            <td className="py-1 pr-3 text-muted">Storyboards</td>
            <td className="py-1 tabular-nums">{r.storyboardsInserted} inserted · {r.storyboardsUpdated} updated</td>
          </tr>
        </tbody>
      </table>
      {/* Skips are the interesting half: each one is this database refusing to
          have a decision overwritten, and the operator should see which. */}
      {r.skipped.length > 0 && (
        <ul className="space-y-0.5 rounded bg-canvas p-2 text-[11px] text-muted">
          {r.skipped.map((m, i) => <li key={i}>· {m}</li>)}
        </ul>
      )}
    </div>
  );

  return (
    <div className="space-y-4 text-sm">
      <p className="text-muted">
        The <strong className="text-fg">generated-media layer</strong> — every clip in the shot library with its tags and
        reuse history, and every storyboard with the plan and who passed each of its two gates. The video files themselves
        already live on Drive and Sanity; this carries the rows that make them findable.{" "}
        <strong className="text-fg">Merge-safe &amp; one-way</strong>: it upserts by durable key and never deletes. A clip
        this database has retired stays retired, and a reel someone here has released stays released — an import can add and
        move things forward, never undo a decision made on this side. Dry-run preview first; a hot backup is taken before
        any real apply.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <a href={api.mediaCatalogueExportUrl} download className="btn-ghost flex items-center gap-1">
          <Database className="h-4 w-4" /> Export media catalogue (JSON)
        </a>
        <label className="btn-ghost flex cursor-pointer items-center gap-1">
          <RefreshCw className="h-4 w-4" /> Import… (preview first)
          <input
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) { setApplied(null); preview.mutate(f); } e.target.value = ""; }}
          />
        </label>
        {preview.isPending && <span className="flex items-center gap-1 text-xs text-muted"><Loader2 className="h-3 w-3 animate-spin" /> previewing…</span>}
      </div>
      {err && <div className="text-xs text-danger">Failed: {err}</div>}

      {staged && (
        <div className="rounded-md border border-warn bg-warn/5 p-3 text-xs">
          <div className="flex items-center gap-1 font-medium"><AlertCircle className="h-3.5 w-3.5 text-warn" /> Dry-run preview — nothing written yet</div>
          <div className="mt-2"><Report r={staged.dry} /></div>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={() => apply.mutate(staged.data)}
              disabled={apply.isPending}
              className="flex items-center gap-1 rounded bg-brand px-3 py-1.5 text-xs text-white hover:bg-brand/90 disabled:opacity-60"
            >
              {apply.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Apply to this database
            </button>
            <button onClick={() => setStaged(null)} className="btn-ghost text-xs">Cancel</button>
          </div>
        </div>
      )}
      {applied && (
        <div className="rounded-md border border-line bg-canvas p-3 text-xs">
          <div className="flex items-center gap-1 font-medium text-accent"><CheckCircle2 className="h-3.5 w-3.5" /> Applied to this database</div>
          <div className="mt-2"><Report r={applied} /></div>
        </div>
      )}
    </div>
  );
}

function BackupPanel() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "backup"],
    queryFn: () => api.backupStatus(),
    refetchInterval: 60_000,
  });
  const run = useMutation({
    mutationFn: () => api.backupRun(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "backup"] }),
  });

  if (q.isPending) return <div className="flex items-center gap-2 text-sm text-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (q.error || !q.data) return <div className="text-sm text-danger">Failed to load backup status.</div>;

  const { status, scheduler } = q.data;
  const newest = status.newest;
  const newestStale = newest && newest.ageDays > 1; // a backup older than 1 day means the cron didn't fire

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-muted" />
          <span className="font-medium">{status.count}</span>
          <span className="text-muted">backups · {(status.totalBytes / 1024 / 1024).toFixed(1)}MB total</span>
        </div>
        <div className="text-xs text-muted">retain: {status.retainCount}</div>
        <div className="text-xs text-muted">cron: <code className="rounded bg-line/40 px-1">{scheduler.cronExpression}</code></div>
        <div className="ml-auto flex items-center gap-2">
          {run.isPending ? (
            <span className="text-xs text-muted flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> backing up…
            </span>
          ) : run.data?.ok ? (
            <span className="text-xs text-accent flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> ran ({run.data.durationMs}ms)
            </span>
          ) : null}
          <button
            onClick={() => run.mutate()}
            disabled={run.isPending || scheduler.inFlight}
            className="btn-ghost flex items-center gap-1 text-sm"
          >
            <RefreshCw className="h-4 w-4" /> Back up now
          </button>
        </div>
      </div>

      {newest ? (
        <div className={`rounded-md border p-3 ${newestStale ? "border-warn bg-warn/5" : "border-line bg-canvas"}`}>
          <div className="flex items-center justify-between text-xs">
            <div className="font-medium">Newest: <span className="font-mono">{newest.filename}</span></div>
            <div className="flex items-center gap-2 text-muted">
              {newestStale && <AlertCircle className="h-3.5 w-3.5 text-warn" />}
              {(newest.bytes / 1024).toFixed(0)}KB · {newest.ageDays === 0 ? "today" : `${newest.ageDays}d ago`}
            </div>
          </div>
          {newestStale && (
            <div className="mt-2 text-xs text-warn">
              Newest backup is more than 1 day old. The nightly cron may have failed — check the Errors page for <code>scheduler.backup</code>.
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-md border border-warn bg-warn/5 p-3 text-xs text-warn">
          No backups have been taken yet. Click <strong>Back up now</strong> to create the first one, or wait for the next cron tick at <code>{scheduler.cronExpression}</code>.
        </div>
      )}

      {q.data.records.length > 1 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted">All {q.data.records.length} backups</summary>
          <table className="mt-2 w-full text-xs">
            <thead className="text-left text-2xs uppercase tracking-wide text-muted">
              <tr>
                <th className="py-1 pr-3">File</th>
                <th className="py-1 pr-3">Size</th>
                <th className="py-1 pr-3">Age</th>
              </tr>
            </thead>
            <tbody>
              {q.data.records.map((r) => (
                <tr key={r.filename} className="border-t border-line/60">
                  <td className="py-1 pr-3 font-mono">{r.filename}</td>
                  <td className="py-1 pr-3 tabular-nums">{(r.bytes / 1024).toFixed(0)}KB</td>
                  <td className="py-1 pr-3 text-muted">{r.ageDays === 0 ? "today" : `${r.ageDays}d ago`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      <p className="text-2xs text-muted">
        Restore manually with <code className="rounded bg-line/40 px-1">node keywords/dist/cli.js backup restore &lt;date-or-filename&gt;</code> — the CLI prints safe-restore instructions; auto-restore is disabled to protect against overwriting the live DB.
      </p>
    </div>
  );
}

function WindowPicker({ value, onChange }: { value: Window; onChange: (w: Window) => void }) {
  const opts: { id: Window; label: string }[] = [
    { id: "mtd", label: "Month-to-date" },
    { id: "7d", label: "Last 7 days" },
    { id: "30d", label: "Last 30 days" },
    { id: "all", label: "All time" },
  ];
  return (
    <div className="flex rounded-md border border-line bg-white p-0.5 text-xs">
      {opts.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={value === o.id ? "rounded bg-brand/10 px-3 py-1.5 text-brand" : "rounded px-3 py-1.5 text-muted hover:text-ink"}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function KpiTile({ icon, label, value, subtle }: { icon: React.ReactNode; label: string; value: string; subtle?: string }) {
  return (
    <div className="rounded-md border border-line bg-white p-4">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted">
        {icon}
        {label}
      </div>
      <div className="mt-3 truncate text-3xl font-semibold">{value}</div>
      {subtle && <div className="mt-1 truncate text-xs text-muted">{subtle}</div>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted">{title}</div>
      <div className="rounded-md border border-line bg-white p-3">{children}</div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-2 py-3 text-sm text-muted">{children}</div>;
}

function BreakdownTable<K extends string>({ rows, total }: { rows: CostSummaryBreakdown<K>[]; total: number }) {
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase tracking-wide text-muted">
        <tr>
          <th className="py-2 pr-3">Key</th>
          <th className="py-2 pr-3">Calls</th>
          <th className="py-2 pr-3">Spend</th>
          <th className="py-2 pr-3">Share</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const pct = total > 0 ? Math.round((r.totalUsd / total) * 100) : 0;
          return (
            <tr key={r.key} className="border-t border-line/60">
              <td className="py-2 pr-3 font-mono text-xs">{r.key}</td>
              <td className="py-2 pr-3 text-xs text-muted">{r.rows}</td>
              <td className="py-2 pr-3 font-medium">${r.totalUsd.toFixed(4)}</td>
              <td className="py-2 pr-3">
                <div className="flex items-center gap-2 text-xs">
                  <div className="h-1.5 w-32 rounded bg-line">
                    <div className="h-1.5 rounded bg-brand" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-muted">{pct}%</span>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Sparkline({ points }: { points: { bucket: string; totalUsd: number; rowCount: number }[] }) {
  const max = Math.max(...points.map((p) => p.totalUsd), 0.0001);
  return (
    <div className="space-y-1">
      {points.map((p) => {
        const pct = max > 0 ? Math.round((p.totalUsd / max) * 100) : 0;
        return (
          <div key={p.bucket} className="flex items-center gap-3 text-xs">
            <div className="w-20 font-mono text-muted">{p.bucket}</div>
            <div className="flex-1">
              <div className="h-2 w-full rounded bg-line">
                <div className="h-2 rounded bg-brand" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <div className="w-20 text-right tabular-nums">${p.totalUsd.toFixed(4)}</div>
            <div className="w-12 text-right text-muted">{p.rowCount}</div>
          </div>
        );
      })}
    </div>
  );
}

function RecentTable({ rows }: { rows: CostLedgerView[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-muted">
          <tr>
            <th className="py-2 pr-3">When</th>
            <th className="py-2 pr-3">Provider</th>
            <th className="py-2 pr-3">Operation</th>
            <th className="py-2 pr-3">Keyword</th>
            <th className="py-2 pr-3">Model</th>
            <th className="py-2 pr-3">Tokens / chars / units</th>
            <th className="py-2 pr-3">Cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-line/60 align-top">
              <td className="py-2 pr-3 text-xs text-muted">
                {new Date(r.occurredAt + (r.occurredAt.endsWith("Z") ? "" : "Z")).toLocaleString("en-GB")}
              </td>
              <td className="py-2 pr-3">
                <span className={providerPillClass(r.provider)}>{r.provider}</span>
              </td>
              <td className="py-2 pr-3 font-mono text-xs">{r.operation}</td>
              <td className="py-2 pr-3 text-xs">
                {r.keywordId ? (
                  <a href={`#/pipeline?keyword=${encodeURIComponent(r.keywordId)}`} className="text-brand hover:underline">
                    {r.keywordId}
                  </a>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td className="py-2 pr-3 font-mono text-xs">{r.model ?? "—"}</td>
              <td className="py-2 pr-3 text-xs text-muted">
                {r.inputTokens != null && r.outputTokens != null
                  ? `${r.inputTokens} in / ${r.outputTokens} out${r.cacheReadTokens ? ` · ${r.cacheReadTokens} cache-read` : ""}`
                  : r.chars != null
                  ? `${r.chars} chars`
                  : `${r.units ?? 1} unit${(r.units ?? 1) === 1 ? "" : "s"}`}
              </td>
              <td className="py-2 pr-3 tabular-nums">${r.estimatedCostUsd.toFixed(5)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function providerPillClass(p: CostProvider): string {
  switch (p) {
    case "anthropic":
    case "bedrock":
      return "pill pill-ok";
    case "elevenlabs":
    case "replicate":
    case "openai":
      return "pill pill-warn";
    case "serpapi":
    case "firecrawl":
    case "dataforseo":
      return "pill pill-muted";
    default:
      return "pill pill-muted";
  }
}

/**
 * Actual AWS-billed Amazon Bedrock cost from Cost Explorer, scoped to the
 * Bedrock region (eu-central-1). Distinct from the token-estimate ledger above.
 */
function AwsBedrockSection() {
  const q = useQuery({
    queryKey: ["cost", "aws-bedrock"],
    queryFn: () => api.awsBedrockCost(30),
    staleTime: 60 * 60 * 1000, // Cost Explorer lags ~1 day + $0.01/call
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return (
    <Section title="AWS Bedrock — actual billed cost">
      {q.isLoading ? (
        <div className="flex items-center gap-2 p-4 text-sm text-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading from AWS Cost Explorer…
        </div>
      ) : q.isError || (q.data && q.data.ok === false) ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Couldn’t read Cost Explorer{q.data?.error ? `: ${q.data.error}` : ""}. This needs
            {" "}<code>ce:GetCostAndUsage</code> and Cost Explorer enabled on the account.
          </span>
        </div>
      ) : q.data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KpiTile
              icon={<DollarSign className="h-5 w-5" />}
              label="Month to date (billed)"
              value={`$${q.data.monthToDate.toFixed(2)}`}
              subtle={`Bedrock · ${q.data.region}`}
            />
            <KpiTile
              icon={<TrendingUp className="h-5 w-5" />}
              label="Last 30 days (billed)"
              value={`$${q.data.total.toFixed(2)}`}
              subtle={`${q.data.currency}`}
            />
            <KpiTile
              icon={<Database className="h-5 w-5" />}
              label="Source"
              value="Cost Explorer"
              subtle={`updated ${q.data.lastUpdated.slice(0, 16).replace("T", " ")}`}
            />
          </div>
          <BedrockDailyBars daily={q.data.daily} currency={q.data.currency} />
          <p className="text-2xs text-muted/70">
            Actual AWS charges for Amazon Bedrock in {q.data.region}. Cost Explorer lags ~1 day,
            so today’s figure is partial. Scoped by region (this app is the {q.data.region}
            {" "}Bedrock user); Bedrock calls can’t be tagged per-app.
          </p>
        </div>
      ) : (
        <Empty>No Bedrock cost data.</Empty>
      )}
    </Section>
  );
}

function BedrockDailyBars({ daily, currency }: { daily: AwsBedrockDailyCost[]; currency: string }) {
  if (!daily.length) return <Empty>No daily cost points.</Empty>;
  const max = Math.max(...daily.map((d) => d.amount), 0.000001);
  const W = 720, H = 120, pad = 4;
  const bw = (W - pad * 2) / daily.length;
  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-white p-3">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-32 w-full min-w-[560px]" preserveAspectRatio="none">
        {daily.map((d, i) => {
          const h = (d.amount / max) * (H - 20);
          return (
            <g key={d.date}>
              <rect
                x={pad + i * bw + 1}
                y={H - 14 - h}
                width={Math.max(bw - 2, 1)}
                height={h}
                rx={1}
                className="fill-brand/70"
              >
                <title>{`${d.date}: $${d.amount.toFixed(4)} ${currency}`}</title>
              </rect>
            </g>
          );
        })}
      </svg>
      <div className="mt-1 flex justify-between text-2xs text-muted/70">
        <span>{daily[0]?.date}</span>
        <span>{daily[daily.length - 1]?.date}</span>
      </div>
    </div>
  );
}
